/**
 * Lazy factory — loads the module only when one of its elements enters the
 * viewport. Uses IntersectionObserver + dynamic import() for both network
 * and parse-time savings on pages with many below-the-fold components.
 *
 * Accepts an optional `root` so it can be re-run against AJAX-injected
 * containers. Initialized elements are marked via `data-js-init` to guarantee
 * each class instantiates at most once per element.
 */

const INIT_ATTR = 'data-js-init';

function isInitialized(element, className) {
  const value = element.getAttribute(INIT_ATTR);
  if (!value) return false;
  return value.split(' ').includes(className);
}

function markInitialized(element, className) {
  const value = element.getAttribute(INIT_ATTR);
  const names = value ? value.split(' ') : [];
  if (!names.includes(className)) {
    names.push(className);
    element.setAttribute(INIT_ATTR, names.join(' '));
  }
}

function instantiate(Class, elements, options) {
  elements.forEach(el => {
    if (isInitialized(el, Class.name)) return;
    new Class(el, options);
    markInitialized(el, Class.name);
  });
}

export default function lazyFactory(importFn, selector, options = {}, root = document) {
  const elements = [...root.querySelectorAll(selector)];
  if (elements.length === 0) return;

  // Fallback for environments without IntersectionObserver (SSR, old tests)
  if (typeof IntersectionObserver === 'undefined') {
    importFn().then(module => instantiate(module.default, elements, options));
    return;
  }

  let loaded = false;
  const observer = new IntersectionObserver(
    (entries) => {
      if (loaded) return;
      const visible = entries.some(entry => entry.isIntersecting);
      if (!visible) return;

      loaded = true;
      observer.disconnect();
      importFn().then(module => instantiate(module.default, elements, options));
    },
    { rootMargin: '200px' } // load slightly before visible
  );

  elements.forEach(el => observer.observe(el));
}
